// ✅ ViewTracking.js - بعد تنسيق التصميم بالكامل
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import '../styles/ViewTracking.css';
import HeaderMenu from '../components/HeaderMenu';


function ViewTracking() {
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audiologistNotes, setAudiologistNotes] = useState('');
  const [hearingModel, setHearingModel] = useState('');
  const [colorSelection, setColorSelection] = useState([]);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const docRef = doc(db, 'jobCards', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCard({ id: docSnap.id, ...data });
          setAudiologistNotes(data.notes || '');
          setHearingModel(data.hearingModel || '');
          setColorSelection(data.colorSelection || []);
        } else {
          alert('Job Card not found!');
        }
      } catch (err) {
        console.error('Error fetching card:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [id]);

  const getCurrentUserEmail = () => auth.currentUser?.email || 'Unknown User';

  const handleFinish = async () => {
    const now = new Date();
    const docRef = doc(db, 'jobCards', card.id);

    await updateDoc(docRef, {
      status: 'done',
      completedAt: now.toISOString(),
      lastUpdated: serverTimestamp(),
      history: arrayUnion({
        user: getCurrentUserEmail(),
        action: 'Marked as Done',
        timestamp: now.toISOString()
      })
    });

    setCard(prev => ({
      ...prev,
      status: 'done',
      completedAt: now.toISOString(),
      history: [...(prev.history || []), {
        user: getCurrentUserEmail(),
        action: 'Marked as Done',
        timestamp: now.toISOString()
      }]
    }));

    alert('Marked as done.');
  };

  const handleSaveAudiology = async () => {
    const now = new Date();
    const docRef = doc(db, 'jobCards', card.id);
    await updateDoc(docRef, {
      hearingModel,
      notes: audiologistNotes,
      colorSelection,
      lastUpdated: serverTimestamp(),
      history: arrayUnion({
        user: getCurrentUserEmail(),
        action: 'Updated hearing info',
        timestamp: now.toISOString()
      })
    });
    alert('Audiology info saved.');
  };

  const toggleColor = (color) => {
    setColorSelection(prev =>
      prev.includes(color) ? prev.filter(c => c !== color) : [...prev, color]
    );
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (!card) return <p className="loading">Job Card not found</p>;

  return (
    <div className="tracking-container">
      <HeaderMenu />
      <h1 className="tracking-title">View Job Card</h1>

      <div className="info-grid">
        <p><strong>Serial:</strong> {card.serial || 'N/A'}</p>
        <p><strong>Patient:</strong> {card.firstName} {card.lastName}</p>
        <p><strong>Age:</strong> {card.age}</p>
        <p><strong>Destination:</strong> {card.destination}</p>
        <p><strong>Status:</strong> <span className="status-label">{card.status}</span></p>
        {card.completedAt && (
          <p><strong>Completed At:</strong> {new Date(card.completedAt).toLocaleString()}</p>
        )}
      </div>

      <h3>Audiogram</h3>
      <table className="audiogram-table">
        <thead>
          <tr>
            <th>Side</th>
            {['250', '500', '1000', '2000', '3000', '4000'].map(freq => (
              <th key={freq}>{freq}Hz</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {['Left', 'Right'].map(side => (
            <tr key={side}>
              <td>{side}</td>
              {['250', '500', '1000', '2000', '3000', '4000'].map(freq => (
                <td key={freq}>{card.audiogram?.[side]?.[freq] || '-'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Hearing Aid Info</h3>
      <p><strong>Model:</strong> {card.hearingModel || '—'}</p>
      <p><strong>Color Selection:</strong> {card.colorSelection?.join(', ') || '—'}</p>

      <h3>Shell Style</h3>
      <p>{Object.keys(card.shellStyle || {}).filter(k => card.shellStyle[k]).join(', ') || '—'}</p>

      <h3>Earmold Material</h3>
      <p>{Object.keys(card.earmoldMaterial || {}).filter(k => card.earmoldMaterial[k]).join(', ') || '—'}</p>

      <h3>Earmold Color</h3>
      <p>{card.earmoldColor || '—'}</p>

      <h3>Venting</h3>
      <p>{Object.keys(card.venting || {}).filter(k => card.venting[k]).join(', ') || '—'}</p>

      <h3>Finish</h3>
      <p>{card.finish || '—'}</p>

      <h3>Shell Options</h3>
      <p>{Object.keys(card.shellOptions || {}).filter(k => card.shellOptions[k]).join(', ') || '—'}</p>

      <h3>Wireless Accessories</h3>
      <p>{Object.keys(card.wirelessAccessories || {}).filter(k => card.wirelessAccessories[k]).join(', ') || '—'}</p>

      <h3>Technician & Audiologist</h3>
      <p><strong>Audiologist:</strong> {card.audiologistName || '—'}</p>
      <p><strong>Technician:</strong> {card.technicianName || '—'}</p>

      <h3>Notes</h3>
      <p>{card.notes || '—'}</p>

      {/* ✅ Attachments */}
      {card.attachments && card.attachments.length > 0 && (
        <div className="attachments-section">
          <h3>Attachments</h3>
          <ul className="attachments-list">
            {card.attachments.map((url, index) => (
              <li key={index}>
                <a
                  href={url}
                  className="download-btn"
                  download={`Attachment-${index + 1}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Attachment {index + 1}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button onClick={handleFinish} className="finish-btn">Finish Job Card</button>

      <h3>History</h3>
      {card.history && card.history.length > 0 ? (
        <ul className="history-list">
          {card.history.map((entry, idx) => (
            <li key={idx}>
              <strong>{entry.user}</strong> - {entry.action}<br />
              <small>{new Date(entry.timestamp).toLocaleString()}</small>
            </li>
          ))}
        </ul>
      ) : (
        <p>No history available.</p>
      )}
    </div>
  );
}

export default ViewTracking;